import React, {
  useMemo,
  useState,
  type FC,
  type Dispatch,
  type SetStateAction,
} from "react";
import { Helper } from "@/app/utils/services/helper/helper";
import type { UserDetailState } from "@/app/utils/services/types/userDetail";
import { WalletProviderType } from "@/app/utils/services/enum/wallet-provider-type";
import Image from "next/image";
import { WALLET_HINTS } from "@/app/utils/constants/constants";
import { WalletConnect } from "@/app/utils/wallet/walletConnect";
import { useWalletService } from "@/app/context/walletServiceContext/walletServiceContext";
import {
  Discord,
  Facebook,
  Google,
  Reddit,
} from "@/app/components/common/Icons";
import { useTheme } from "next-themes";

type Props = {
  setOpen: Dispatch<SetStateAction<boolean>>;
  startNewSearch?: boolean;
};
const ConnectDialog: FC<Props> = (props) => {
  const { setOpen } = props;
  const [showHints, setShowHints] = useState(false);
  const { resolvedTheme } = useTheme();
  const { customWalletService, initiateCustomAuthChallenge } =
    useWalletService();
  const walletConnect = useMemo(() => new WalletConnect(), []);
  const WALLET_PROVIDERS = Helper.WALLET_PROVIDERS;
  const nonSocialWalletProviders = WALLET_PROVIDERS.filter(
    (provider) => !provider.isSocial
  );
  const toggleHints = () => {
    setShowHints(!showHints);
  };

  const connectWallet = async (walletProviderType: WalletProviderType) => {
    if (walletProviderType === WalletProviderType.WALLET_CONNECT) {
      await customWalletService.connectWallet(walletProviderType);
      const response = await walletConnect.connectWithWalletConnect();
      if (response) {
        setOpen(false);
        //@ts-ignore
        await initiateCustomAuthChallenge(response);
      }
      return;
    }

    const response =
      await customWalletService.connectWallet(walletProviderType);
    setOpen(false);
    //@ts-ignore
    await initiateCustomAuthChallenge(response);
  };

  return (
    <>
      <div>
        <div className="modal-overlay z-40">
          <div className="max-w-[556px] relative shadow rounded-2xl p-[2px] bg-gradient-to-r from-primary to-secondary">
            <div className="rounded-2xl flex flex-col items-center justify-center m-0 w-full bg-lightwhite dark:bg-sleap-gray-950  dark:text-lightwhite text-sleap-gray-950 ">
              <div className="overflow-y-auto rounded-2xl max-h-[80vh] p-8 gallery-modal">
                <div className="flex md:p-0 justify-end absolute w-full -right-3 top-5">
                  <button onClick={() => setOpen(false)}>
                    <Image
                      src="/assets/v2/icons/close-icon-white.svg"
                      alt="Sleap.io"
                      width={30}
                      height={30}
                      className="h-6 w-6 me-10 mt-2 dark:block hidden"
                    />
                    <Image
                      src="/assets/v2/icons/close-icon-light.svg"
                      alt="Sleap.io"
                      width={30}
                      height={30}
                      className="h-6 w-6 me-10 mt-2 dark:hidden block"
                    />
                  </button>
                </div>
                <div className="flex items-center gap-2.33 mb-2 justify-start w-full">
                  {resolvedTheme === "dark" ? (
                    <Image
                      src="/assets/v2/icons/sleap-icon-light.svg"
                      alt="Sleap.io"
                      width={30}
                      height={30}
                      className="h-8 w-8 mr-2"
                    />
                  ) : (
                    <Image
                      src="/assets/v2/icons/sleap-icon-dark.svg"
                      alt="Sleap.io"
                      width={30}
                      height={30}
                      className="h-8 w-8 mr-2"
                    />
                  )}

                  <h3 className="text-xl font-semibold dark:text-lightwhite text-sleap-gray-950 ">
                    Connect with Sleap.io
                  </h3>
                </div>
                <p className="text-base dark:text-gray-400 text-sleap-gray-700 mb-6 w-full">
                  Select a provider to connect or create a new wallet.
                </p>

                <div className="wallet-options grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                  {nonSocialWalletProviders.map(
                    (walletProvider, index: number) => (
                      <div key={index} className="cursor-pointer">
                        {!walletProvider.isSocial && (
                          <div
                            className="wallet-option flex-col relative dark:bg-gray-800 bg-white rounded-2xl p-4 flex justify-center items-center shadow"
                            onClick={() => connectWallet(walletProvider.type)}
                            role="button"
                          >
                            <div className="wallet-icon absolute top-3 right-3">
                              <Image
                                src="/assets/v2/icons/mdi_open-in-new.png"
                                width={30}
                                height={30}
                                className="w-[20px] h-[20px]"
                                alt="mdi-open"
                              />
                            </div>
                            <Image
                              src={`/assets/v2/icons/${walletProvider.icon}`}
                              alt="walletProvider.name"
                              width={30}
                              height={30}
                              className="mb-2 w-[32px] h-[32px]"
                            />
                            <span className="dark:text-lightwhite text-sleap-gray-950">
                              {walletProvider.name}
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>

                <div className="divider text-center ">
                  <span className="text-gray-600">or</span>
                </div>

                {WALLET_PROVIDERS.map(
                  (walletProvider, index: number) =>
                    walletProvider.isSocial && (
                      <div key={index}>
                        <div
                          className="social-login cursor-point dark:bg-gray-800 bg-white rounded-2xl p-4 mb-6 flex shadow"
                          onClick={() => {
                            connectWallet(walletProvider.type);
                          }}
                          role="button"
                        >
                          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                            <Facebook
                              color={
                                resolvedTheme == "light"
                                  ? "lightblack"
                                  : "offWhite"
                              }
                            />
                            <Reddit
                              color={
                                resolvedTheme == "light"
                                  ? "lightblack"
                                  : "offWhite"
                              }
                            />
                            <Google
                              color={
                                resolvedTheme == "light"
                                  ? "lightblack"
                                  : "offWhite"
                              }
                            />
                            <Discord
                              color={
                                resolvedTheme == "light"
                                  ? "lightblack"
                                  : "offWhite"
                              }
                            />
                            {/* <Image
                              src="/assets/v2/icons/reddit-icon-white.svg"
                              width={28}
                              height={28}
                              className="w-8"
                              alt="Google Logo"
                            />
                            <Image
                              src="/assets/v2/icons/google-icon-white.svg"
                              width={28}
                              height={28}
                              className="w-8"
                              alt="Twitter Logo"
                            />
                            <Image
                              src="/assets/v2/icons/discord-icon-white.svg"
                              width={28}
                              height={28}
                              className="w-8"
                              alt="Discord"
                            /> */}
                          </div>
                          <div className="flex-grow">
                            <p className="font-semibold dark:text-lightwhite text-sleap-gray-950 mb-1">
                              Create Wallet with Social Login
                            </p>
                            <p className="dark:text-gray-400 text-sleap-gray-700 text-sm mt-1">
                              The wallet serves as an account to store and
                              manage your digital assets on the blockchain.
                            </p>
                          </div>
                          <div className="flex items-center">
                            <Image
                              src="/assets/v2/icons/right-arrow.svg"
                              width={30}
                              height={30}
                              className="w-[17px]"
                              alt="right-arrow"
                            />
                          </div>
                        </div>
                      </div>
                    )
                )}

                <div className="mt-6">
                  <h4 className="dark:text-lightwhite text-sleap-gray-950 ">
                    Why do we need a wallet?
                  </h4>
                  <p className="mt-2 text-sm dark:text-gray-400 text-sleap-gray-700">
                    The wallet serves as an account for you to store and manage
                    your digital assets on the blockchain. We do not store any
                    data related to your social logins.
                    <span
                      onClick={() => toggleHints()}
                      className="cursor-pointer focus:ring text-primary hover:underline ms-1"
                      role="button"
                    >
                      Learn more
                    </span>
                  </p>
                </div>

                <div
                  className={`mt-4 flex flex-col gap-y-4 ${
                    !showHints && "hidden"
                  }`}
                >
                  {WALLET_HINTS.map((walletHint, index: number) => (
                    <div key={index} className="wallet-hint">
                      <Image
                        width={40}
                        height={40}
                        src={`${walletHint.icon}`}
                        alt=""
                      />
                      <div className="hint-info">
                        <div className="hint-label dark:text-lightwhite text-sleap-gray-950">
                          {walletHint.label}
                        </div>
                        <div className="hint-desc dark:text-gray-400 text-sleap-gray-700">
                          {walletHint.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default ConnectDialog;
